import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { StarRounded as Star } from '@mui/icons-material';
import TableDropdownMenu from '../../global/TableDropdownMenu';
import { formatDollarAmount } from '../../../utils/global/helper';
import { ProductInView } from '../../../utils/catalog/catalogFilters';
import { CatalogVariant } from '../../../types/catalog';
import { ItemWithCommissions } from '../../../types/items';
import ItemThumbnail from './ItemThumbnail';
import { HIDE_ON_PHONE, THUMB_GAP, libraryRowSx } from './layout';

interface Props {
  entry: ProductInView;
  variant: CatalogVariant;
  /** Undefined while the items list catches up with the catalog */
  item: ItemWithCommissions | undefined;
  busy: boolean;
  actions: React.ComponentProps<typeof TableDropdownMenu>['options'];
  onSelect: (variantId: string) => void;
}

export default function LibraryVariantRow({
  entry,
  variant,
  item,
  busy,
  actions,
  onSelect,
}: Props) {
  const theme = useTheme();
  const label = variant.variantLabel ?? entry.product.name;

  return (
    <Box
      onClick={() => onSelect(variant.id)}
      sx={{
        ...libraryRowSx,
        cursor: 'pointer',
        '&:hover': { bgcolor: theme.palette.accent.main },
      }}>
      <span />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: THUMB_GAP,
          minWidth: 0,
          pl: 3,
        }}>
        <ItemThumbnail url={variant.imageUrl} name={label} />
        <Box minWidth={0}>
          <Typography variant="body2" noWrap>
            {item?.favoritedAt && (
              <Star
                sx={{
                  display: 'inline-block',
                  fontSize: theme.typography.body1.fontSize,
                  // eslint-disable-next-line no-restricted-syntax -- typography-exception: favourite-star yellow is a decorative icon colour with no palette token
                  color: '#FDCC0D',
                  verticalAlign: 'text-bottom',
                  mr: 0.5,
                }}
              />
            )}
            {label}
          </Typography>
          {item?.sku && (
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              display="block">
              {item.sku}
            </Typography>
          )}
        </Box>
      </Box>
      <Box component="span" sx={{ display: HIDE_ON_PHONE }} />
      <Typography variant="body2" textAlign="right" noWrap>
        {formatDollarAmount(variant.price)}
      </Typography>
      <Box onClick={e => e.stopPropagation()}>
        {item && <TableDropdownMenu disabled={busy} options={actions} />}
      </Box>
    </Box>
  );
}
